import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, contactos, CRM_CONTACTOS_URL } from "../api/client";
import { useCatalogo } from "../api/queries";
import { useEditor } from "../state/editor";
import { useConfirmar } from "./Dialogos";
import type { Contacto, Sitio } from "../domain/types";
import { nuevaPieza, normalizar } from "../domain/model";
import { Campo, ErrorAviso, Modal, Paginacion } from "./ui";
import Icono from "./Icono";
function BuscarContactos({
  seleccionar,
  onClose,
}: {
  seleccionar: (c: Contacto) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(""),
    [debounced, setDebounced] = useState(""),
    [page, setPage] = useState(0),
    [todos, setTodos] = useState(false);
  // Cada búsqueda es una llamada a GHL (límite compartido por todo el
  // equipo): se espera a que el vendedor deje de escribir.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(text), 500);
    return () => clearTimeout(t);
  }, [text]);
  const busqueda = todos ? "" : debounced;
  const q = useQuery({
    queryKey: ["contactos", todos, busqueda],
    queryFn: ({ signal }) => contactos(busqueda, todos, signal),
    enabled: todos || debounced.length >= 2,
    // Sin caché: el vendedor puede crear el contacto en el CRM (enlace de
    // abajo) y volver a esta pestaña; tiene que aparecer al instante.
    staleTime: 0,
    refetchOnWindowFocus: true,
    // Un 429 ya lo reintentó el backend con espera; repetirlo aquí solo
    // gasta más del límite de GHL.
    retry: (fallos, error) =>
      !(error instanceof ApiError && error.status === 429) && fallos < 1,
  });
  const rows = (q.data ?? []).filter(
    (c) =>
      !todos ||
      normalizar(`${c.nombre} ${c.empresa}`).includes(normalizar(text)),
  );
  return (
    <Modal title="Elegir contacto" onClose={onClose}>
      <Campo label="Buscar contacto">
        <input
          autoFocus
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setPage(0);
          }}
          placeholder="Nombre, correo o teléfono"
        />
      </Campo>
      <label className="check">
        <input
          type="checkbox"
          checked={todos}
          onChange={(e) => {
            setTodos(e.target.checked);
            setPage(0);
          }}
        />
        Ver todos los contactos
      </label>
      {q.isFetching ? <p role="status">Buscando…</p> : null}
      <ErrorAviso error={q.error} />
      <ul className="choices">
        {rows.slice(page * 20, page * 20 + 20).map((c) => (
          <li key={c.id}>
            <button
              onClick={() => {
                seleccionar(c);
                onClose();
              }}
            >
              <strong>{c.nombre || "Sin nombre"}</strong>
              <small>
                {[c.empresa, c.telefono, c.correo].filter(Boolean).join(" · ")}
              </small>
            </button>
          </li>
        ))}
      </ul>
      {q.data && !rows.length ? <p>No se encontraron contactos.</p> : null}
      <Paginacion page={page} total={rows.length} onChange={setPage} />
      <CrearEnCrm destacado={Boolean(q.data && !rows.length)} />
    </Modal>
  );
}
// Abre los contactos del CRM en otra pestaña para crear el que falta. Al
// volver, la búsqueda se refresca sola (ver staleTime/refetchOnWindowFocus).
function CrearEnCrm({ destacado = false }: { destacado?: boolean }) {
  return (
    <p className={destacado ? "notice" : "muted"}>
      {destacado ? "¿El contacto todavía no existe? " : null}
      <a href={CRM_CONTACTOS_URL} target="_blank" rel="noopener noreferrer">
        <Icono nombre="agregar" /> Crear contacto en el CRM
      </a>
      {destacado ? " — créalo allí y vuelve a buscarlo aquí." : null}
    </p>
  );
}
export default function Cliente() {
  const cliente = useEditor((s) => s.cotizacion.cliente),
    patch = useEditor((s) => s.patch),
    catalogo = useCatalogo();
  const [modal, setModal] = useState(false);
  const cambiar = (key: keyof typeof cliente, value: string) =>
    patch({
      cliente: { ...cliente, [key]: value },
      ...(key === "encargado"
        ? { contacto: null, correoEnvio: "", telefonoEnvio: "" }
        : {}),
    });
  const confirmar = useConfirmar();
  const marcas = [
    ...new Set(
      [
        "Promart",
        "Produbanco",
        "Ecuajugos",
        "Fybeca",
        "SanaSana",
        "OkiDoki",
        ...(catalogo.data?.preciosContrato.map((p) => p.clienteContrato) ?? []),
        ...(catalogo.data?.catalogoClienteEspecial.map((p) => p.clienteMarca) ??
          []),
      ].map(normalizar),
    ),
  ];
  async function cambiarContrato(marca: string) {
    const editor = useEditor.getState();
    const especiales = editor
      .todas()
      .some((c) =>
        c.sitios.some((s) =>
          s.productos.some((pr) =>
            pr.piezas.some(
              (p) =>
                p.catalogoEspecial &&
                normalizar(p.catalogoEspecial.clienteMarca) !== marca,
            ),
          ),
        ),
      );
    if (
      especiales &&
      !(await confirmar({
        titulo: "Cambiar de cliente",
        mensaje:
          "Se eliminará la selección de las piezas del catálogo especial del cliente actual.",
        confirmar: "Cambiar de cliente",
      }))
    )
      return;
    const actualizar = (sitios: Sitio[]) =>
      sitios.map((s) => ({
        ...s,
        productos: s.productos.map((pr) => ({
          ...pr,
          piezas: pr.piezas.map((p) =>
            p.catalogoEspecial &&
            normalizar(p.catalogoEspecial.clienteMarca) !== marca
              ? { ...nuevaPieza(), id: p.id }
              : {
                  ...p,
                  precioContratado:
                    catalogo.data?.preciosContrato.find(
                      (pc) =>
                        normalizar(pc.clienteContrato) === marca &&
                        pc.familia === p.familia &&
                        pc.variante === p.variante,
                    )?.precioContratado ?? null,
                },
          ),
        })),
      }));
    useEditor.setState((s) => ({
      cotizacion: {
        ...s.cotizacion,
        cliente: { ...s.cotizacion.cliente, cliente_contrato: marca },
        sitios: actualizar(s.cotizacion.sitios),
      },
      propuestas: s.propuestas.map((p) => ({
        ...p,
        sitios: actualizar(
          p.numero === s.cotizacion.numero ? s.cotizacion.sitios : p.sitios,
        ),
      })),
      dirty: true,
    }));
  }
  return (
    <section className="card">
      <p className="eyebrow">Paso 1</p>
      <h2>Datos del cliente</h2>
      <div className="grid two">
        <Campo label="Nombre del cliente">
          <input
            required
            value={cliente.nombre}
            onChange={(e) => cambiar("nombre", e.target.value)}
          />
        </Campo>
        <Campo label="Encargado">
          <input
            value={cliente.encargado}
            onChange={(e) => cambiar("encargado", e.target.value)}
          />
        </Campo>
        <div className="row">
          <button onClick={() => setModal(true)}>
            <Icono nombre="buscar" /> Buscar en contactos
          </button>
          <CrearEnCrm />
        </div>
        <Campo label="Nombre del proyecto">
          <input
            value={cliente.nombre_proyecto}
            onChange={(e) => cambiar("nombre_proyecto", e.target.value)}
          />
        </Campo>
        <Campo label="Dirección de facturación">
          <input
            value={cliente.direccion_facturacion}
            onChange={(e) => cambiar("direccion_facturacion", e.target.value)}
          />
        </Campo>
        <Campo label="Cliente con lista de precios especial">
          <select
            value={normalizar(cliente.cliente_contrato)}
            onChange={(e) => void cambiarContrato(e.target.value)}
          >
            <option value="ninguno">Ninguno</option>
            {marcas.map((m) => (
              <option key={m} value={m}>
                {(
                  {
                    promart: "Promart",
                    produbanco: "Produbanco",
                    ecuajugos: "Ecuajugos",
                    fybeca: "Fybeca",
                    sanasana: "SanaSana",
                    okidoki: "OkiDoki",
                    austro: "Austro",
                  } as Record<string, string>
                )[m] ?? m}
              </option>
            ))}
          </select>
        </Campo>
      </div>
      {modal ? (
        <BuscarContactos
          onClose={() => setModal(false)}
          seleccionar={(contacto) =>
            patch({
              contacto,
              cliente: {
                ...cliente,
                encargado: contacto.nombre,
                nombre: contacto.empresa || cliente.nombre,
                direccion_facturacion:
                  contacto.direccion || cliente.direccion_facturacion,
                nombre_proyecto: contacto.proyecto || cliente.nombre_proyecto,
              },
              correoEnvio: contacto.correo || "",
              telefonoEnvio: contacto.telefono || "",
            })
          }
        />
      ) : null}
    </section>
  );
}
